import type { Topic } from '@pepper/utils/src/types/seo'
import type { Keyword } from 'types/keyword'

interface FormattedTopicRow {
  'Sr. No.': number
  Topic: string
  'Primary KW': string
  'P KW V': number
  [key: `Secondary KW ${number}`]: string
  [key: `S. KW${number} V`]: number
  'Potential Traffic Lower-end': number
  'Potential Traffic Upper-end': number
}

interface FormattedKeywordRow {
  'Sr. No.': number
  Keyword: string
  'KW search volume': string | number
  Intent: string
  Difficulty: string
}

const createColumnHeaders = (maxSecondaryKeywords: number): string[] => {
  const baseHeaders = [
    'Sr. No.',
    'Topic',
    'Primary KW',
    'P KW V',
    'Potential Traffic Lower-end',
    'Potential Traffic Upper-end',
  ]

  for (let i = 1; i <= maxSecondaryKeywords; i++) {
    baseHeaders.push(`Secondary KW ${i}`, `S. KW${i} V`)
  }
  return baseHeaders
}

export const formatTopicsForExport = (topics: Topic[]): FormattedTopicRow[] => {
  return topics.map((topic, index) => {
    const row: FormattedTopicRow = {
      'Sr. No.': index + 1,
      Topic: topic.name,
      'Primary KW': '',
      'P KW V': 0,
      'Potential Traffic Lower-end': topic.expected_traffic_start ?? 0,
      'Potential Traffic Upper-end': topic.expected_traffic_end ?? 0,
    }

    const primaryKeyword = topic.keywords?.find(kw => kw.type === 'primary')
    if (primaryKeyword) {
      row['Primary KW'] = primaryKeyword.keyword
      row['P KW V'] = primaryKeyword.volume
    }

    const secondaryKeyword = topic.keywords?.filter(
      kw => kw.type === 'secondary'
    )

    secondaryKeyword?.forEach((kw, idx) => {
      const num = idx + 1
      row[`Secondary KW ${num}`] = kw.keyword
      row[`S. KW${num} V`] = kw.volume
    })

    return row
  })
}

export const formatKeywordsForExport = (
  keywords: Keyword[]
): FormattedKeywordRow[] => {
  return keywords.map((keyword, index) => ({
    'Sr. No.': index + 1,
    Keyword: keyword.name,
    'KW search volume': keyword.volume,
    Intent: getIntentLabel(keyword.intent),
    Difficulty: `${keyword.difficulty_percentage}%`,
  }))
}

const getIntentLabel = (intent: string): string => {
  switch (intent) {
    case '0':
      return 'Commercial'
    case '1':
      return 'Informational'
    case '2':
      return 'Navigational'
    case '3':
      return 'Transactional'
    default:
      return ''
  }
}

export const getKeywordsExportProps = (keywords: Keyword[]) => {
  const formattedData = formatKeywordsForExport(keywords)
  return {
    data: formattedData,
    headers: [
      { label: 'Sr. No.', key: 'Sr. No.' },
      { label: 'Keyword', key: 'Keyword' },
      { label: 'KW search volume', key: 'KW search volume' },
      { label: 'Intent', key: 'Intent' },
      { label: 'Difficulty', key: 'Difficulty' },
    ],
  }
}

export const getTopicsExportProps = (topics: Topic[]) => {
  const maxSecondaryKws = Math.max(
    ...topics.map(
      topic => topic.keywords?.filter(kw => kw.type === 'secondary').length || 0
    )
  )
  const formattedData = formatTopicsForExport(topics)
  const headersArray = createColumnHeaders(maxSecondaryKws)
  return {
    data: formattedData,
    headers: headersArray.map(header => ({ label: header, key: header })),
  }
}
